import { forkEntry } from './ad-versions';
import type {
  BannerKitVersion,
  BannerKitVersionEntry,
  KitBox,
  KitLayer,
  KitLayerRole,
  KitLayers,
  KitLayerText,
  KitLayerTransform,
  KitLayerTransformInput,
} from './banner-kit-api';

/**
 * Kit layers: the pure geometry and bookkeeping of the layer editor. A decomposed shape render is a base image
 * plus transparent layer PNGs, each positioned by `box_px` in RENDER pixels (the shape's render size, e.g.
 * 2912x360 for 728x90). The owner's edits are per-layer transforms, also in render pixels, so the editor's stage
 * (the render scaled down by `s`) and the backend flatten agree on one coordinate system.
 *
 * The composition rule (the editor shows it, the backend flatten must draw it the same way): the base first, then
 * every visible layer in ascending `z`. A layer PNG is drawn into its `box_px` rect (stretched to it), or over the
 * whole render when the PNG is a full-canvas layer (its natural size equals the base's). It is scaled by `scale`
 * about the CENTRE of its box, then moved by `(dx, dy)` render pixels. A `hidden` layer is skipped.
 */

/* ---------- transforms ---------- */

/** An untouched layer. */
export const DEFAULT_LAYER_TRANSFORM: KitLayerTransform = { dx: 0, dy: 0, scale: 1, hidden: false };

/** The scale range the editor offers (25 % to 300 %). */
export const MIN_LAYER_SCALE = 0.25;
export const MAX_LAYER_SCALE = 3;

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** A scale inside the offered range; a missing, zero or broken value is 1 (Go's zero value never shrinks a layer
 *  to nothing). Pure. */
export const clampLayerScale = (scale: unknown): number => {
  if (!finite(scale) || scale <= 0) return 1;
  return Math.min(MAX_LAYER_SCALE, Math.max(MIN_LAYER_SCALE, scale));
};

/** A transform with every field present and sane (whole render pixels, a usable scale). Pure. */
export const normaliseLayerTransform = (raw: Partial<KitLayerTransform> | null | undefined): KitLayerTransform => ({
  dx: finite(raw?.dx) ? Math.round(raw.dx) : 0,
  dy: finite(raw?.dy) ? Math.round(raw.dy) : 0,
  scale: clampLayerScale(raw?.scale),
  hidden: raw?.hidden === true,
});

const normaliseBox = (raw: unknown): KitBox => {
  const b = Array.isArray(raw) ? raw : [];
  const n = (i: number): number => (finite(b[i]) ? b[i] : 0);
  const [l, t, r, bottom] = [n(0), n(1), n(2), n(3)];
  return [Math.min(l, r), Math.min(t, bottom), Math.max(l, r), Math.max(t, bottom)];
};

/** The layers bottom to top (ascending `z`; ties keep their order). Pure. */
export const sortLayersByZ = (items: readonly KitLayer[]): KitLayer[] =>
  items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => a.item.z - b.item.z || a.i - b.i)
    .map(({ item }) => item);

/** A decomposed layer list ready to edit: every transform and box sane, sorted bottom to top by `z`. Pure. */
export const normaliseLayerItems = (items: readonly KitLayer[] | null | undefined): KitLayer[] =>
  sortLayersByZ(
    (items ?? [])
      .filter(
        (item) => typeof item?.id === 'string' && item.id !== '' && typeof item.url === 'string' && item.url !== '',
      )
      .map((item) => ({
        ...item,
        box_px: normaliseBox(item.box_px),
        transform: normaliseLayerTransform(item.transform),
      })),
  );

/** Whether a layer carries copy (its text can be rewritten). Pure. */
export const isTextLayerRole = (role: KitLayerRole): boolean =>
  role === 'headline' || role === 'subhead' || role === 'cta';

/** Whether a layer may be restyled: everything but the logo, which is the brand's and is reproduced exactly. Pure. */
export const isRestylableRole = (role: KitLayerRole): boolean => role !== 'logo';

/* ---------- geometry ---------- */

export interface LayerSize {
  w: number;
  h: number;
}

export interface LayerRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface LayerPoint {
  x: number;
  y: number;
}

/** The factor that fits the render into `max` (stage px per render px); 0 when either size is unusable. Pure. */
export const stageScale = (render: LayerSize, max: LayerSize): number => {
  if (!(render.w > 0 && render.h > 0 && max.w > 0 && max.h > 0)) return 0;
  return Math.min(max.w / render.w, max.h / render.h);
};

/** Render px to stage px. Pure. */
export const toStage = (renderPx: number, s: number): number => renderPx * s;

/** Stage px to render px (whole pixels, as the flatten takes them). Pure. */
export const toRender = (stagePx: number, s: number): number => (s > 0 ? Math.round(stagePx / s) : 0);

/** A box as a rect (render px). Pure. */
export const boxRect = (box: KitBox): LayerRect => ({
  left: box[0],
  top: box[1],
  width: Math.max(0, box[2] - box[0]),
  height: Math.max(0, box[3] - box[1]),
});

/** A box's centre (render px): the point a layer scales about. Pure. */
export const boxCentre = (box: KitBox): LayerPoint => ({ x: (box[0] + box[2]) / 2, y: (box[1] + box[3]) / 2 });

/** Two sizes agree within 1 % on both sides (a re-encoded full-canvas PNG may be off by a pixel). */
const sameSize = (a: LayerSize, b: LayerSize): boolean =>
  a.w > 0 && a.h > 0 && b.w > 0 && b.h > 0 && Math.abs(a.w / b.w - 1) <= 0.01 && Math.abs(a.h / b.h - 1) <= 0.01;

/** Whether a layer PNG covers the whole canvas (its natural size is the base's) rather than just its box. Unknown
 *  sizes (not loaded yet) read as a box layer. Pure. */
export const isFullCanvasLayer = (natural: LayerSize | undefined, baseNatural: LayerSize | undefined): boolean =>
  Boolean(natural && baseNatural && sameSize(natural, baseNatural));

/** Where a layer PNG is drawn before its transform (render px): its box, or the whole render for a full-canvas
 *  layer. Pure. */
export const layerFrame = (
  item: Pick<KitLayer, 'box_px'>,
  render: LayerSize,
  natural?: LayerSize,
  baseNatural?: LayerSize,
): LayerRect =>
  isFullCanvasLayer(natural, baseNatural)
    ? { left: 0, top: 0, width: render.w, height: render.h }
    : boxRect(item.box_px);

/** Where the layer's box sits once transformed (render px): scaled about its centre, then moved. This is where the
 *  owner sees the element, whichever way the PNG is framed. Pure. */
export const transformedBox = (item: Pick<KitLayer, 'box_px' | 'transform'>): LayerRect => {
  const { width, height } = boxRect(item.box_px);
  const c = boxCentre(item.box_px);
  const scale = clampLayerScale(item.transform.scale);
  const w = width * scale;
  const h = height * scale;
  return { left: c.x - w / 2 + item.transform.dx, top: c.y - h / 2 + item.transform.dy, width: w, height: h };
};

/** A layer's CSS placement on the stage (stage px). */
export interface LayerStageStyle {
  left: number;
  top: number;
  width: number;
  height: number;
  transform: string;
  transformOrigin: string;
}

/** The CSS placement of a layer on the stage: its frame in stage px, and a transform that scales it about its box
 *  centre and then moves it (CSS applies `translate(...) scale(...)` right to left about the origin). Pure. */
export const layerStageStyle = (
  item: Pick<KitLayer, 'box_px' | 'transform'>,
  s: number,
  render: LayerSize,
  natural?: LayerSize,
  baseNatural?: LayerSize,
): LayerStageStyle => {
  const frame = layerFrame(item, render, natural, baseNatural);
  const c = boxCentre(item.box_px);
  const t = item.transform;
  const round = (n: number): number => Math.round(n * 100) / 100;
  return {
    left: round(frame.left * s),
    top: round(frame.top * s),
    width: round(frame.width * s),
    height: round(frame.height * s),
    transform: `translate(${round(t.dx * s)}px, ${round(t.dy * s)}px) scale(${clampLayerScale(t.scale)})`,
    transformOrigin: `${round((c.x - frame.left) * s)}px ${round((c.y - frame.top) * s)}px`,
  };
};

/** Keep a move inside the render: the layer's box centre may not leave the canvas (a layer dragged off it could
 *  never be grabbed again). Pure. */
const clampMove = (box: KitBox, render: LayerSize, dx: number, dy: number): { dx: number; dy: number } => {
  const c = boxCentre(box);
  const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
  return {
    dx: Math.round(render.w > 0 ? clamp(dx, -c.x, render.w - c.x) : dx),
    dy: Math.round(render.h > 0 ? clamp(dy, -c.y, render.h - c.y) : dy),
  };
};

/** The transform after dragging a layer by `delta` STAGE px from where the drag started (`origin`). Pure. */
export const dragTransform = (
  origin: KitLayerTransform,
  delta: LayerPoint,
  s: number,
  box: KitBox,
  render: LayerSize,
): KitLayerTransform => {
  if (!(s > 0)) return origin;
  const moved = clampMove(box, render, origin.dx + delta.x / s, origin.dy + delta.y / s);
  return { ...origin, ...moved };
};

/** The transform after nudging a layer by `delta` RENDER px (the arrow keys). Pure. */
export const nudgeTransform = (
  origin: KitLayerTransform,
  delta: LayerPoint,
  box: KitBox,
  render: LayerSize,
): KitLayerTransform => ({ ...origin, ...clampMove(box, render, origin.dx + delta.x, origin.dy + delta.y) });

/* ---------- order and edits ---------- */

/** Bring a layer one step forward (towards the top) or backward, renumbering `z` 1..n bottom to top. Unchanged at
 *  either end or for an unknown id. Pure. */
export const moveLayer = (items: readonly KitLayer[], id: string, direction: 'forward' | 'backward'): KitLayer[] => {
  const ordered = sortLayersByZ(items);
  const at = ordered.findIndex((item) => item.id === id);
  const to = direction === 'forward' ? at + 1 : at - 1;
  if (at === -1 || to < 0 || to >= ordered.length) return ordered;
  const next = ordered.slice();
  [next[at], next[to]] = [next[to], next[at]];
  return next.map((item, i) => ({ ...item, z: i + 1 }));
};

/** Replace one layer's transform (merged with `patch`). Pure. */
export const withTransform = (items: readonly KitLayer[], id: string, patch: Partial<KitLayerTransform>): KitLayer[] =>
  items.map((item) => (item.id === id ? { ...item, transform: { ...item.transform, ...patch } } : item));

/** Replace one layer's image (a text change or restyle), and its text record for a text change. Pure. */
export const withLayerImage = (items: readonly KitLayer[], id: string, url: string, text?: KitLayerText): KitLayer[] =>
  items.map((item) => (item.id === id ? { ...item, url, ...(text ? { text } : {}) } : item));

const idsByZ = (items: readonly KitLayer[]): string =>
  sortLayersByZ(items)
    .map((item) => item.id)
    .join('\n');

/** Whether the stacking order differs (the flatten takes no `z`, so a new order must be saved on a version first). */
export const zOrderChanged = (a: readonly KitLayer[], b: readonly KitLayer[]): boolean => idsByZ(a) !== idsByZ(b);

/** Whether any layer's image differs (a text change or restyle). Pure. */
export const layerImagesChanged = (a: readonly KitLayer[], b: readonly KitLayer[]): boolean => {
  const urls = new Map(a.map((item) => [item.id, item.url]));
  return b.length !== a.length || b.some((item) => urls.get(item.id) !== item.url);
};

/** Whether any layer's transform differs. Pure. */
export const transformsChanged = (a: readonly KitLayer[], b: readonly KitLayer[]): boolean => {
  const byId = new Map(a.map((item) => [item.id, normaliseLayerTransform(item.transform)]));
  return b.some((item) => {
    const was = byId.get(item.id) ?? DEFAULT_LAYER_TRANSFORM;
    const now = normaliseLayerTransform(item.transform);
    return was.dx !== now.dx || was.dy !== now.dy || was.scale !== now.scale || was.hidden !== now.hidden;
  });
};

/** The flatten body's `transforms`: one per layer, bottom to top. Pure. */
export const transformsFor = (items: readonly KitLayer[]): KitLayerTransformInput[] =>
  sortLayersByZ(items).map((item) => ({ id: item.id, ...normaliseLayerTransform(item.transform) }));

/** What a text-layer check read back: the read for the layer's role, else the only read there is. Pure. */
export const layerReadOf = (
  read: Record<string, string> | null | undefined,
  role: KitLayerRole,
): string | undefined => {
  if (!read) return undefined;
  if (typeof read[role] === 'string') return read[role];
  const values = Object.values(read).filter((v): v is string => typeof v === 'string');
  return values.length === 1 ? values[0] : undefined;
};

/* ---------- versions ---------- */

/** The version entry that keeps edited layers: a deep copy of `base` (params, hero, every render) whose `shape`
 *  render carries `items` as its layers. Saved as a NEW version (the backend never rewrites one in place). Pure. */
export const layersEntry = (
  base: BannerKitVersion,
  shape: string,
  items: readonly KitLayer[],
): BannerKitVersionEntry => {
  const entry = forkEntry(base);
  const render = entry.shapes[shape];
  if (!render?.layers) return entry;
  const layers: KitLayers = { ...render.layers, items: sortLayersByZ(items).map((item) => ({ ...item })) };
  return { ...entry, shapes: { ...entry.shapes, [shape]: { ...render, layers } } };
};

/** The version the flatten wrote, as the backend writes it: `base` forked, the shape's render replaced by the
 *  flattened image (same size and check) carrying the transformed layers, locked. Shown at once, before a refetch.
 *  Pure. */
export const flattenedVersion = (
  base: BannerKitVersion,
  shape: string,
  items: readonly KitLayer[],
  at: string,
  url: string,
): BannerKitVersion => {
  const entry = layersEntry(base, shape, items);
  const render = entry.shapes[shape] ?? {};
  return { ...entry, created_at: at, locked: true, shapes: { ...entry.shapes, [shape]: { ...render, url } } };
};
